/**
 * web/src/games/nocturne/icons.tsx — the nine Nocturne location icons.
 *
 * Hand-drawn replacements for the reference client's icon package: each is a
 * 24-unit-grid stroke icon (strokeWidth 1.35, currentColor), so the game
 * ships no icon dependency. Paths are the approved set, verbatim.
 */
import type { ReactElement, ReactNode } from 'react';

export interface NocturneIconProps {
  size?: number;
}

function Svg({ size = 26, children }: NocturneIconProps & { children: ReactNode }): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.35}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** champagne coupe */
export function VelvetBarIcon({ size }: NocturneIconProps): ReactElement {
  return (
    <Svg size={size}>
      <path d="M5.6 4.5h12.8c0 4.3-2.9 6.9-6.4 6.9S5.6 8.8 5.6 4.5Z" />
      <path d="M12 11.4V18" />
      <path d="M8.4 20h7.2" />
    </Svg>
  );
}

/** quaver pair */
export function JazzCellarIcon({ size }: NocturneIconProps): ReactElement {
  return (
    <Svg size={size}>
      <path d="M7.6 16.6V5.9l8.2-1.9v10.2" />
      <circle cx="5.6" cy="16.6" r="2" />
      <circle cx="13.8" cy="14.4" r="2" />
    </Svg>
  );
}

/** flat roof terrace + railing */
export function RooftopGardenIcon({ size }: NocturneIconProps): ReactElement {
  return (
    <Svg size={size}>
      <path d="M3.6 12.6h16.8v1.8H3.6z" />
      <path d="M5.6 12.6V7.4M9.9 12.6V7.4M14.2 12.6V7.4M18.5 12.6V7.4" />
      <path d="M4.6 7.4h15" />
      <path d="M6.4 14.4V20M17.6 14.4V20" />
    </Svg>
  );
}

/** framed landscape */
export function GalleryIcon({ size }: NocturneIconProps): ReactElement {
  return (
    <Svg size={size}>
      <rect x="3.8" y="4" width="16.4" height="16" rx="1.4" />
      <circle cx="8.9" cy="9.1" r="1.5" />
      <path d="M3.8 16.2 9 11.4l3.4 3.1 3.6-4.1 4.2 4.3" />
    </Svg>
  );
}

/** concierge bell */
export function GrandHotelIcon({ size }: NocturneIconProps): ReactElement {
  return (
    <Svg size={size}>
      <path d="M6 15.2a6 6 0 0 1 12 0" />
      <path d="M3.8 15.2h16.4v1.6H3.8z" />
      <path d="M12 15.2v1.6" />
      <path d="M12 9.2V7.4" />
      <path d="M12 7.4a1 1 0 1 1 .01 0Z" />
    </Svg>
  );
}

/** sailing boat on water */
export function OldDocksIcon({ size }: NocturneIconProps): ReactElement {
  return (
    <Svg size={size}>
      <path d="M3.4 14.6h17.2l-3 4.6H6.4l-3-4.6Z" />
      <path d="M11.6 14.2V3.6" />
      <path d="M11.6 4.6 18.4 13.4h-6.8Z" />
      <path d="M11.6 6.2 6.2 13.4h5.4" />
      <path d="M2.6 21.4c1.6-1.1 3.2-1.1 4.8 0s3.2 1.1 4.8 0 3.2-1.1 4.8 0 3.2 1.1 4.8 0" />
    </Svg>
  );
}

/** open book */
export function BellBookIcon({ size }: NocturneIconProps): ReactElement {
  return (
    <Svg size={size}>
      <path d="M12 7.2C9.6 5.9 6.9 5.9 4.4 7.2v12.2c2.5-1.3 5.2-1.3 7.6 0" />
      <path d="M12 7.2c2.4-1.3 5.1-1.3 7.6 0v12.2c-2.5-1.3-5.2-1.3-7.6 0" />
      <path d="M12 7.2v12.2" />
    </Svg>
  );
}

/** tree */
export function VauxParkIcon({ size }: NocturneIconProps): ReactElement {
  return (
    <Svg size={size}>
      <path d="M12 3.6c3.1 0 5.2 2 5.2 4.4 1.8.4 2.9 1.9 2.9 3.5 0 2-1.8 3.7-4 3.7H7.9c-2.2 0-4-1.7-4-3.7 0-1.6 1.1-3.1 2.9-3.5 0-2.4 2.1-4.4 5.2-4.4Z" />
      <path d="M12 15.2v4.6" />
      <path d="M9.6 20.4h4.8" />
    </Svg>
  );
}

/** proscenium stage, drawn curtains */
export function MeridianIcon({ size }: NocturneIconProps): ReactElement {
  return (
    <Svg size={size}>
      <path d="M4 20V6.6A2.2 2.2 0 0 1 6.2 4.4h11.6A2.2 2.2 0 0 1 20 6.6V20" />
      <path d="M8.2 4.6c-1.6 4.6-1.6 10.6 0 15.2" />
      <path d="M15.8 4.6c1.6 4.6 1.6 10.6 0 15.2" />
      <path d="M3 20h18" />
      <path d="M4.2 4.4h15.6" />
    </Svg>
  );
}

export type NocturneIconKey =
  | 'velvet-bar'
  | 'jazz-cellar'
  | 'rooftop-garden'
  | 'gallery'
  | 'grand-hotel'
  | 'old-docks'
  | 'bell-book'
  | 'vaux-park'
  | 'meridian';

export const NOCTURNE_ICONS: Record<NocturneIconKey, (props: NocturneIconProps) => ReactElement> = {
  'velvet-bar': VelvetBarIcon,
  'jazz-cellar': JazzCellarIcon,
  'rooftop-garden': RooftopGardenIcon,
  'gallery': GalleryIcon,
  'grand-hotel': GrandHotelIcon,
  'old-docks': OldDocksIcon,
  'bell-book': BellBookIcon,
  'vaux-park': VauxParkIcon,
  'meridian': MeridianIcon,
};
